import { useCallback } from 'react';

import { type SubmitHandler, useForm } from 'react-hook-form';

import { createVariable } from '@polyai/stream/store/reducers/variables';
import { selectVariables } from '@polyai/stream/store/reducers/variables/selectors';
import { variablesReferencesAdapter } from '@polyai/stream/store/reducers/variables/utils';
import { ConfirmationModal } from '@polyai/ui/components/molecules/ConfirmationModal';

import { useServerDispatch } from 'components/organisms/AgentStream/context/AgentStreamContext';
import { useAppSelector } from 'hooks/reduxHooks';
import { generateVariableId } from 'lib/prettyUuid';
import { validateVariableName } from './utils';
import VariableModalContent from './VariableModalContent';

interface VariablePopupProps {
  onCreate?: (data: { id: string; name: string }) => void;
  onCancel?: () => void;
}

interface VariableForm {
  name: string;
}

export function VariableCreatePopup({
  onCreate,
  onCancel,
}: VariablePopupProps) {
  const serverDispatch = useServerDispatch();
  const variables = useAppSelector(selectVariables);

  const {
    register,
    formState: { isValid, errors },
    handleSubmit,
  } = useForm<VariableForm>({
    mode: 'onChange',
    defaultValues: {
      name: '',
    },
  });

  const validate = (name: string) => validateVariableName(name, variables);

  const onSubmit: SubmitHandler<VariableForm> = useCallback(
    data => {
      if (!isValid) {
        return;
      }

      const variableId = generateVariableId();

      void serverDispatch(
        createVariable({
          id: variableId,
          name: data.name,
          references: variablesReferencesAdapter.createInitialState(),
        }),
      );

      onCreate?.({ id: variableId, name: data.name });
    },
    [isValid, onCreate, serverDispatch],
  );

  return (
    <ConfirmationModal
      buttonText="Save"
      cancelText="Cancel"
      confirmDisabled={!isValid}
      content={
        <VariableModalContent
          errorMessage={errors.name?.message}
          inputTestId="variable-name-input"
          register={register('name', {
            validate,
            required: {
              message: 'Variable name is required',
              value: true,
            },
          })}
        />
      }
      size="md"
      title="Add variable"
      open
      skipCloseAndConfirm
      onCancelButtonClick={onCancel}
      onConfirmButtonClick={handleSubmit(onSubmit)}
    />
  );
}

export default VariableCreatePopup;
